import { useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { parseMetadata } from '../utils'
import { useMemo } from 'react'
import { Nullable } from '../../../../../common/Nullable'
import { PieChart, PieChartProps } from 'react-minimal-pie-chart'
import './MeetingOverallSentiment.scss'
import { Circle } from './Circle'
import { roundDistributionTo100 } from './helper'

export type OverallSentimentChartOptions = {
  data: PieChartProps['data']
  overall: number
  distribution: {
    [key in SentimentType]: number
  }
}

type SentimentType = 'positive' | 'neutral' | 'negative'
export type SentimentAnalysisMetadata = {
  sentimentAnalysis:
    | {
        overall: number
        distribution: {
          [key in SentimentType]: number
        }
      }
    | { [key: string]: any }
}

export const MeetingOverallSentiment = () => {
  const roomSession = useAppSelector(selectRoomSession)

  const colorMap: {
    [key in SentimentType]: string
  } = {
    positive: '#5227CC',
    neutral: '#FF7E21',
    negative: '#FF0051',
  }

  const sentimentLabels: { [key in SentimentType]: string } = {
    positive: 'Positive',
    neutral: 'Neutral',
    negative: 'Negative',
  }

  const keys = Object.keys(colorMap) as SentimentType[]

  const sentimentAnalysisChartData = useMemo((): Nullable<OverallSentimentChartOptions> => {
    if (!roomSession) {
      return null
    }

    const metadata = parseMetadata(roomSession.metadata) as SentimentAnalysisMetadata
    const distribution = metadata?.sentimentAnalysis?.distribution ?? null
    const overall = metadata?.sentimentAnalysis?.overall ?? null

    if (!overall || !distribution) {
      return null
    }

    if (distribution) {
      const rounded = roundDistributionTo100(distribution)

      return {
        overall,
        distribution: rounded,
        data: keys.map((key) => ({
          title: sentimentLabels[key],
          value: rounded[key],
          color: colorMap[key],
        })),
      }
    }

    return null
  }, [roomSession])
  return (
    <div>
      {sentimentAnalysisChartData && (
        <div className='d-flex flex-row align-items-center gap-4 '>
          <div className='sentiment-chart-container'>
            <PieChart
              data={sentimentAnalysisChartData.data}
              lineWidth={30}
              style={{ width: '100px', height: '100px' }}
              labelPosition={50}
              startAngle={320}
              labelStyle={{ fontSize: '4px', color: '#fff' }}
            ></PieChart>
            <div className='pie-chart-overall-center-text'>
              <div className='pie-chart-overall-inner-text'>
                <div className='pie-chart-overall-value'>{sentimentAnalysisChartData.overall}</div>
              </div>
            </div>
          </div>
          <div className='chart-legend d-flex flex-row align-items-center gap-3'>
            {keys.map((key) => (
              <div className='d-flex flex-row align-items-center gap-1' key={key}>
                <Circle fill={colorMap[key]} key={key} /> {sentimentLabels[key]} {sentimentAnalysisChartData.distribution[key]}%
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
